import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import { Drawer, IconButton } from '@mui/material'
import parse from 'html-react-parser'
import { useEffect, useState } from 'react'
import { getFaq } from 'src/consumer/services/getFaq'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { formFAQDataWithRespectiveFAQCategories } from '../PrewallAndPaywall/faq_utils'
import {
  FAQDrawerPaper,
  FAQDrawerHeader,
  FAQDrawerTitle,
  FAQDrawerCloseBtn,
  FAQDrawerBody,
  FAQCategoryBlock,
  FAQCategoryTitle,
  FAQAccordionWrap,
  FAQAccordionSummary,
  FAQAccordionDetails,
  FAQEmptyMessage,
  FAQSpinnerWrap,
} from './FAQDrawerModernStyles'

export function FAQDrawerModern({ categoryIds, open, onClose }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [openIndex, setOpenIndex] = useState(null) // { categoryIdx, faqIdx }

  useEffect(() => {
    if (!open || !Array.isArray(categoryIds) || categoryIds.length === 0) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const searchString = categoryIds.join(',')

    getFaq(searchString, { withCategoryDetails: true, size: 999 })
      .then((response) => {
        if (cancelled) return
        if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
          const faqContent = response?.data?.data?.faqPage?.content
          const faqCategoryList = response?.data?.data?.faqCategoryList
          const formed = formFAQDataWithRespectiveFAQCategories(faqContent, faqCategoryList)
          setCategories(formed || [])
        } else {
          setCategories([])
        }
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, categoryIds])

  useEffect(() => {
    if (!open) setOpenIndex(null)
  }, [open])

  const toggleFaq = (categoryIdx, faqIdx) => {
    setOpenIndex((prev) => {
      if (prev?.categoryIdx === categoryIdx && prev?.faqIdx === faqIdx) return null
      return { categoryIdx, faqIdx }
    })
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          maxHeight: '88vh',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
        },
      }}
      PaperProps={{
        className: 'faq-drawer-paper',
      }}
    >
      <FAQDrawerPaper>
        <FAQDrawerHeader>
          <FAQDrawerTitle>Frequently asked questions</FAQDrawerTitle>
          <FAQDrawerCloseBtn aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </FAQDrawerCloseBtn>
        </FAQDrawerHeader>

        <FAQDrawerBody>
          {loading ? (
            <FAQSpinnerWrap>
              <div className="faq-spinner" />
            </FAQSpinnerWrap>
          ) : categories.length === 0 ? (
            <FAQEmptyMessage>No FAQs available at the moment.</FAQEmptyMessage>
          ) : (
            categories.map((category, categoryIdx) => {
              const faqs = category?.faqs || []
              if (faqs.length === 0) return null
              return (
                <FAQCategoryBlock key={category?.id || categoryIdx}>
                  <FAQCategoryTitle>{category?.title}</FAQCategoryTitle>
                  {faqs.map((faq, faqIdx) => {
                    const isExpanded =
                      openIndex?.categoryIdx === categoryIdx && openIndex?.faqIdx === faqIdx
                    return (
                      <FAQAccordionWrap key={faq?.id || faqIdx}>
                        <FAQAccordionSummary
                          onClick={() => toggleFaq(categoryIdx, faqIdx)}
                          $expanded={isExpanded}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggleFaq(categoryIdx, faqIdx)
                            }
                          }}
                        >
                          <span className="faq-question">{faq?.question}</span>
                          <ExpandMoreIcon className="faq-expand-icon" />
                        </FAQAccordionSummary>
                        {isExpanded && (
                          <FAQAccordionDetails>
                            <div className="faq-answer">{parse(faq?.answer || '')}</div>
                          </FAQAccordionDetails>
                        )}
                      </FAQAccordionWrap>
                    )
                  })}
                </FAQCategoryBlock>
              )
            })
          )}
        </FAQDrawerBody>
      </FAQDrawerPaper>
    </Drawer>
  )
}
